'use client';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import verificationImage from "@/public/assets/verification-image.svg";
import Image from "next/image";
import AutoSwipeRed from "@/app/components/AutoSwipeRed";
import errorImage from "@/public/assets/error-image.svg";
import { toast } from "sonner";
import usePasswordRecoveryStore from "@/app/stores/passwordRecoveryStore";
import NEXT_PUBLIC_BASE_URL from "@/config";

interface FormData {
    otp: string;
    email: string;
}

export default function PasswordRecovery() {
    const router = useRouter();
    const { register, handleSubmit, formState: { isValid, errors }, getValues, reset: formReset } = useForm<FormData>({
        defaultValues: {
            otp: '',
            email: ''
        }
    });

    const {
        isSubmitting,
        errorMessage,
        successMessage,
        showErrorModal,
        successModal,
        resendMessage,
        isResending,
        resendModal,
        storedEmail,
        setIsSubmitting,
        setErrorMessage,
        setSuccessMessage,
        setShowErrorModal,
        setSuccessModal,
        setResendMessage,
        setIsResending,
        setResendModal,
        setStoredEmail,
        reset: storeReset
    } = usePasswordRecoveryStore();

    const resendCode = async () => {
        const email = storedEmail || getValues('email');
        if (!email) {
            toast.error("Please enter your email to resend the code.");
            return;
        }
        setIsResending(true);
        setResendMessage(null);
    
        try {
            const response = await fetch(`${NEXT_PUBLIC_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
    
            const result = await response.json();
            setIsResending(false);
    
            if (response.ok) {
                setShowErrorModal(false);
                setResendMessage('Verification code has been resent to your email.');
                setResendModal(true);
                formReset({ otp: '' });
                const inputs = document.querySelectorAll('input[id^="otp-"]') as NodeListOf<HTMLInputElement>;
                inputs.forEach(input => (input.value = ''));
            } else {
                if (response.status === 404) {
                    setResendMessage('Email does not exist');
                } else {
                    setResendMessage(result.message || 'Failed to resend the code. Please try again later.');
                }
            }
        } catch (error) {
            setIsResending(false);
            setResendMessage('Error sending the code. Please try again later.');
        }
    };

    const onSubmit = async (data: FormData) => {
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            const response = await fetch(`${NEXT_PUBLIC_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: data.email })
            });

            const result = await response.json();
            setIsSubmitting(false);

            if (!response.ok) {
               if (response.status === 404) {
                    setErrorMessage('Email does not exist');
                } else {
                    setErrorMessage(result.message || 'Request failed, please try again');
                }
                return;
            }

            setStoredEmail(data.email);
            localStorage.setItem('passwordRecoveryEmail', data.email); 
            setSuccessMessage("We have sent a 5-digit code to your registered email address. Please click the button below to verify your account.");
            setSuccessModal(true);
            formReset();
        } catch (error) {
            console.error('Error during password recovery:', error);
            setIsSubmitting(false);
            setErrorMessage('Request failed. Please try again.');
        }
    };

    return (
        <div className="h-screen items-center justify-center flex flex-col md:flex-row">
            <AutoSwipeRed startIndex={1} />
            {/* Signup Form */}
            <div className="w-full md:w-1/2 flex justify-center flex-col items-center px-6 bg-white">
                <form className="w-full max-w-md" onSubmit={handleSubmit(onSubmit)}>
                    <h1 className="text-lg sm:text-xl md:text-2xl font-medium text-black mb-4 md:pt-0 pt-4">Password Recovery</h1>
                    <p className="text-black-light mb-12 font-normal text-base">
                        Let&apos;s help you access your account again by entering your registered email address.
                        </p>
                    {errorMessage && <p className="text-primary mb-4">{errorMessage}</p>}
                    
                    {/* Email */}
                    <div className="mb-12">
                        <label className="mb-4 block text-base text-black-light">Enter your Email Address</label>
                        <Input
                            type="email"
                            placeholder="e.g Adeyo@gmail.com"
                            {...register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[\w-\.]+(\+[\w0-9]+)?@([\w-]+\.)+[\w-]{2,4}$/g,
                                    message: 'Enter a valid email'
                                }
                            })}
                            className={`w-full min-h-11 mt-1 rounded-full placeholder:text-placeholder-gray ${errors.email ? 'border-primary' : 'border-black-dark'}`}
                        />
                        {errors.email && <p className="text-primary">{errors.email.message}</p>}
                    </div>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        disabled={!isValid || isSubmitting}
                        className={`w-full mb-6 border-black-dark min-h-11 mt-1 rounded-full h-12 bg-primary text-white ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Checking...' : 'Reset Password'}
                    </Button>
                    <div className="flex justify-start text-base text-[#333333] py-2">
                        Remember Password? 
                            <span className="">
                                <a href="/auth/login" className="text-primary font-bold underline ml-1">Login.</a>
                            </span>
                        </div>
                </form>
            </div>
            {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
         <div className="bg-white p-8 rounded-3xl shadow-lg w-full max-w-lg text-center">
                {/* Image */}
                <div className="mb-4">
                    <Image
                        src={errorImage}
                        alt="Verification failed"
                        width={120}
                        height={120}
                        className="mx-auto"
                    />
                </div>
                {/* Heading */}
                <h1 className="text-3xl font-semibold text-black mb-4">Incorrect Code</h1>
                {/* Message */}
                <p className="text-base text-black-light leading-loose mb-8">
                {errorMessage}
                </p>

                {/* Submit Button */}
                <Button
                    type="button"
                    onClick={() => {
                        formReset({ otp: '' }); 
                        setShowErrorModal(false);
                        const inputs = document.querySelectorAll('input[id^="otp-"]') as NodeListOf<HTMLInputElement>;
                        inputs.forEach((input) => (input.value = ''));
                    }}
                    className="w-3/4 mb-6 h-12 bg-[#F44336] text-white rounded-full"
                >
                   Try Again
                </Button>

                <p className="text-base text-black-light font-semibold cursor-pointer"
                 onClick={resendCode}
                >
                        Resend Code
                </p>
            </div>
    </div>
)}

            {successModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            {/* verification */}
            <div className="bg-white p-6 rounded-3xl shadow-lg w-full max-w-lg text-center">
                {/* Image */}
                <div className="mb-4">
                    <Image
                        src={verificationImage}
                        alt="Verification Illustration"
                        width={120}
                        height={120}
                        className="mx-auto"
                    />
                </div>
                {/* Heading */}
                <h1 className="text-3xl font-semibold text-black mb-2">Verification Code</h1>
                {/* Message */}
                <p className="text-base text-black-light p-6 mb-4">
                    {successMessage}
                </p>
               
                {/* Submit Button */}
                <Button
                    type="button"
                    onClick={() => router.push("/auth/password-recovery/otpRecovery")}
                    className="w-3/4 h-12 bg-[#4CAF50] text-white rounded-full"
                >
                    Enter Verification Code
                </Button>
                <div className="text-sm text-black-light mx-12 mb-4 mt-4">
                    If you don&apos;t see the email in your inbox, please check your spam or junk folder..
                </div>
                <p className="text-base text-black-light font-semibold cursor-pointer" onClick={resendCode}>
                        Resend Code
                    </p>
            </div>
    </div>
)}
        </div>
    );
}